/**
 * @lodex-pro/web-admin/AdminShell — PLATFORM_ADMIN console chrome (Task 25.1, DEC-111).
 *
 * A lean, tenant-LESS shell (Paelode operates globally — there is no active-tenant
 * banner like web-dispatch's AppShell). 240px left nav with the "Paelode Systems Admin"
 * wordmark, the Phase-25 nav sections, and Sign Out at the bottom; a 56px top bar
 * with the page title, the signed-in admin email, and a "Paelode Admin" role badge.
 * Reuses web-shared's NavItem for consistent active-state styling + the shared dark
 * design tokens.
 *
 * Must render inside <BrowserRouter> (uses useLocation/useNavigate).
 */
import * as React from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { Toaster } from 'sonner';
import {
  NavItem,
  roleDisplayLabel,
  SHELL_HEADER_HEIGHT_PX,
  TOASTER_OFFSET,
} from '@lodex-pro/web-shared';
import { ADMIN_NAV } from './nav';
import { PlaceholderPage } from './pages/PlaceholderPage';
import { AccountPage } from './pages/AccountPage';
import { TeamPage } from './features/team/TeamPage';
import { TenantsPage } from './features/tenants/TenantsPage';
import { TenantDetailPage } from './features/tenants/TenantDetailPage';
import { OperatorsPage } from './features/operators/OperatorsPage';
import { OperatorDetailPage } from './features/operators/OperatorDetailPage';
import { ReviewDuplicatesPage } from './features/operators/ReviewDuplicatesPage';
import { WellsPage } from './features/wells/WellsPage';
import { WellDetailPage } from './features/wells/WellDetailPage';
import { CatalogSyncPage } from './features/catalog-sync/CatalogSyncPage';
import { TankCatalogPage } from './features/tank-catalog/TankCatalogPage';
import { SiteTypesPage } from './features/site-types/SiteTypesPage';
import { SupportEmailPage } from './features/support-email/SupportEmailPage';
import { AbbreviationsPage } from './features/abbreviations/AbbreviationsPage';
import { MapUsagePage } from './features/map-usage/MapUsagePage';
import { BillingPage } from './features/billing/BillingPage';
import { DashboardPage } from './features/dashboard/DashboardPage';
// #56 PR 1b (DEC-362) — the ops console.
import { OpsPage } from './features/ops/OpsPage';
import { IncidentPage } from './features/ops/AlertsTabs';
import { DisposalPage } from './features/disposal/DisposalPage';

/** Active nav id for a pathname ('/' is exact; the rest match by prefix). */
function activeIdFor(pathname: string): string {
  const match = ADMIN_NAV.find((n) =>
    n.path === '/' ? pathname === '/' : pathname.startsWith(n.path),
  );
  return match?.id ?? 'dashboard';
}

export interface AdminShellProps {
  readonly userEmail: string;
  /** The signed-in admin's role code — shown as the badge pill (mapped to a human label). */
  readonly role?: string | null;
  readonly onSignOut: () => void;
}

export function AdminShell({ userEmail, role, onSignOut }: AdminShellProps): React.JSX.Element {
  const location = useLocation();
  // Real role label ('Platform Admin'), falling back to the console brand when absent.
  const roleBadge = roleDisplayLabel(role) ?? 'Paelode Admin';
  const navigate = useNavigate();
  const activeId = activeIdFor(location.pathname);
  const pageTitle = ADMIN_NAV.find((n) => n.id === activeId)?.label ?? 'Dashboard';

  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        backgroundColor: 'var(--color-bg-primary)',
        color: 'var(--color-text-primary)',
        fontFamily: 'var(--font-sans, system-ui, sans-serif)',
      }}
    >
      {/* ── Left nav (240px) ── */}
      <nav
        aria-label="Admin navigation"
        style={{
          width: '240px',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'var(--color-bg-rail)',
        }}
      >
        <div
          style={{
            height: '56px',
            display: 'flex',
            alignItems: 'center',
            padding: '0 24px',
            fontSize: '17px',
            fontWeight: 700,
          }}
        >
          Paelode&nbsp;Systems{' '}
          <span style={{ color: 'var(--color-text-tertiary)', fontWeight: 400, marginLeft: '6px' }}>
            Admin
          </span>
        </div>

        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '8px 12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
          }}
        >
          {ADMIN_NAV.map((n) => (
            <NavItem
              key={n.id}
              label={n.label}
              icon={n.icon}
              active={n.id === activeId}
              onClick={(): void => void navigate(n.path)}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={onSignOut}
          aria-label="Sign out"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            height: '44px',
            margin: '8px 12px 12px',
            padding: '0 14px',
            border: 'none',
            borderRadius: '12px',
            background: 'transparent',
            color: 'var(--color-text-secondary)',
            cursor: 'pointer',
            fontSize: '15px',
            fontFamily: 'inherit',
          }}
        >
          <LogOut size={18} aria-hidden="true" /> Sign Out
        </button>
      </nav>

      {/* ── Main column ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Top bar — height shared with web-shared's AppShell so the toast
            offset below stays correct for both shells. */}
        <header
          style={{
            height: `${SHELL_HEADER_HEIGHT_PX}px`,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 20px',
            backgroundColor: 'var(--color-bg-rail)',
          }}
        >
          <h1 style={{ fontSize: '20px', fontWeight: 600, margin: 0 }}>{pageTitle}</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '14px', color: 'var(--color-text-tertiary)' }}>
              {userEmail}
            </span>
            <span
              style={{
                fontSize: '14px',
                fontWeight: 500,
                color: 'var(--color-text-secondary)',
                backgroundColor: 'var(--color-bg-tertiary)',
                borderRadius: 'var(--radius-pill)',
                padding: '6px 14px',
              }}
            >
              {roleBadge}
            </span>
            <button
              type="button"
              onClick={onSignOut}
              aria-label="Sign out"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                background: 'none',
                border: 'none',
                color: 'var(--color-text-secondary)',
                cursor: 'pointer',
              }}
            >
              <LogOut size={18} aria-hidden="true" />
            </button>
          </div>
        </header>

        {/* Routed content */}
        <main style={{ flex: 1, overflowY: 'auto' }}>
          <Routes>
            {/* Tenants (25.2) + Operators (25.3) are built; the rest are placeholders. */}
            <Route path="/tenants" element={<TenantsPage />} />
            <Route path="/tenants/:id" element={<TenantDetailPage />} />
            <Route path="/operators" element={<OperatorsPage />} />
            <Route path="/operators/duplicates" element={<ReviewDuplicatesPage />} />
            <Route path="/operators/:id" element={<OperatorDetailPage />} />
            {/* Phase 21 / Task 21.7 follow-up — disposal companies + sign-up queue. */}
            <Route path="/disposal" element={<DisposalPage />} />
            <Route path="/wells" element={<WellsPage />} />
            <Route path="/wells/:id" element={<WellDetailPage />} />
            <Route path="/catalog-sync" element={<CatalogSyncPage />} />
            <Route path="/tank-catalog" element={<TankCatalogPage />} />
            <Route path="/site-types" element={<SiteTypesPage />} />
            <Route path="/support-email" element={<SupportEmailPage />} />
            <Route path="/abbreviations" element={<AbbreviationsPage />} />
            {/* 86baefu3r — per-tenant Mapbox map-load counts (reporting only). */}
            <Route path="/map-usage" element={<MapUsagePage />} />
            {/* Quick 260827-pwd — self-service password change (no forgot-password). */}
            {/* #56 PR 1b (DEC-362) — Operations: Health + Errors. */}
            <Route path="/ops" element={<OpsPage />} />
            {/* #56 PR 2c (DEC-363) — the page the alert email links to. */}
            <Route path="/ops/incidents/:id" element={<IncidentPage />} />
            {/* Phase 21 / Task 21.7 — Billing, its own tabbed routes. */}
            <Route path="/billing/*" element={<BillingPage />} />
            <Route path="/team" element={<TeamPage />} />
            <Route path="/account" element={<AccountPage />} />
            {/* Dashboard (25.8) is the landing page at '/'. */}
            <Route path="/" element={<DashboardPage />} />
            {ADMIN_NAV.filter(
              (n) =>
                n.id !== 'dashboard' &&
                n.id !== 'tenants' &&
                n.id !== 'operators' &&
                n.id !== 'wells' &&
                n.id !== 'catalog-sync' &&
                n.id !== 'tank-catalog' &&
                n.id !== 'abbreviations' &&
                // 86baefu3r — real page above; without this exclusion the placeholder
                // would register /map-usage a second time.
                n.id !== 'map-usage' &&
                n.id !== 'ops' &&
                n.id !== 'billing' &&
                n.id !== 'disposal',
            ).map((n) => (
              <Route
                key={n.id}
                path={n.path}
                element={
                  <PlaceholderPage
                    icon={n.icon}
                    title={n.label}
                    description={n.description}
                    task={n.task}
                  />
                }
              />
            ))}
          </Routes>
        </main>
      </div>
      <Toaster position="top-right" theme="dark" offset={TOASTER_OFFSET} />
    </div>
  );
}
